import { Badge, ListItemDecorator } from '@mui/joy';
import { forwardRef, useImperativeHandle } from 'react';
import classNames from 'classnames';
import { createPortal } from 'react-dom';

import { type DefaultElementTypes, type ValueType } from '../core/Tabs.types';
import {
  type TabConfigItem,
  type TabsMenuActionsContainerProps,
  type TabsMenuActionsProps,
  type TabsMenuActionsSlotProps,
  type TabsMenuInnerProps,
  type TabsMenuItemProps,
  type TabsMenuListContainerProps,
  type TabsMenuProps,
  type TabsMenuWithNavHandle,
} from './TabsMenu.types';

import {
  ACTIONS_CONTAINER_DIRECTION_CLASSES,
  DEFAULT_MENU_CLASSES,
  DISPLAY_NAMES,
  DIVIDER_CLASS,
  MENU_DATA_ATTRIBUTES,
} from './TabsMenu.data';

import { typedForwardRef } from '@/shared/utils/react';

import {
  useTabsConfigList,
  useTabsContext,
  useTabsControl,
  useTabsIndexControl,
  useTabsNavigationEvents,
  useTranslateLabel,
} from './TabsMenu.hooks';
import useListStepper from '@/shared/hooks/useListStepper';

import Tabs from '../core/Tabs';
import TabsMenuContextProvider from './TabsMenu.context.Provider';

type TabNodes = {
  tabPanels: React.ReactNode[];
  tabs: React.ReactNode[];
};

const TabsMenuActionsContainer = forwardRef<HTMLDivElement, TabsMenuActionsContainerProps>(
  ({ className, reverse, ...rest }, ref) => (
    <div
      ref={ref}
      {...rest}
      className={classNames(
        DEFAULT_MENU_CLASSES.ACTIONS_CONTAINER,
        reverse
          ? ACTIONS_CONTAINER_DIRECTION_CLASSES.REVERSE
          : ACTIONS_CONTAINER_DIRECTION_CLASSES.DEFAULT,
        className,
      )}
    />
  ),
);

TabsMenuActionsContainer.displayName = DISPLAY_NAMES.ACTIONS_CONTAINER;

const TabsMenuActions = forwardRef<HTMLDivElement, TabsMenuActionsProps>(
  ({ children, className, render, ...containerProps }, ref) => {
    const ctx = useTabsContext();

    const inCtx = !!ctx;

    const actionsNode = (
      <TabsMenuActionsContainer ref={ref} {...containerProps} className={typeof className === 'function' ? className(inCtx) : className}>
        {render ? render(inCtx) : children}
      </TabsMenuActionsContainer>
    );

    if (!inCtx) return actionsNode;

    return ctx.actionsSlot ? createPortal(actionsNode, ctx.actionsSlot) : null;
  },
);

TabsMenuActions.displayName = DISPLAY_NAMES.ACTIONS;

const TabsMenuActionsSlot: React.FC<TabsMenuActionsSlotProps> = ({ children, ...rest }) => {
  const ctx = useTabsContext();

  return (
    <TabsMenuActionsContainer ref={ctx?.registerActionSlot} {...rest} reverse>
      {!!children && <TabsMenuActionsContainer>{children}</TabsMenuActionsContainer>}
    </TabsMenuActionsContainer>
  );
};

const TabsMenuItem = <T extends React.ElementType = DefaultElementTypes['Tab']>({
  as,
  badgeContent,
  badgeContentMax = 999,
  children,
  endDecorator,
  startDecorator,
  ...rest
}: TabsMenuItemProps<T>) => {
  const badgeProps = (() => {
    const visible = !!badgeContent;

    return {
      [MENU_DATA_ATTRIBUTES.VISIBLE]: visible,
      badgeContent,
      invisible: !visible,
      max: badgeContentMax,
    };
  })();

  return (
    <Tabs.Tab as={as ?? 'button'} {...rest}>
      {!!startDecorator && (
        <ListItemDecorator className="mr-[-0.75rem] empty:hidden">
          {startDecorator}
        </ListItemDecorator>
      )}
      <Badge
        {...badgeProps}
        sx={{
          '& .MuiBadge-badge': {
            fontSize: '0.6rem',
            position: 'absolute',
            right: '-8px',
            top: '-8px',
          },
        }}
      >
        {children}
      </Badge>
      {!!endDecorator && (
        <ListItemDecorator className="empty:hidden">{endDecorator}</ListItemDecorator>
      )}
    </Tabs.Tab>
  );
};

const TabsMenuListContainer: React.FC<TabsMenuListContainerProps> = ({ className, ...rest }) => (
  <div {...rest} className={classNames(DEFAULT_MENU_CLASSES.LIST_CONTAINER, className)} />
);

const BaseTabsMenu = <T extends ValueType>({
  actions,
  activeDefaultTab,
  activeDefaultTabIndex,
  activeTab,
  activeTabIndex,
  beforeList,
  children,
  config,
  divider,
  hidePanelContent,
  onChange,
  renderTabContent,
  renderTabPanelContent,
}: TabsMenuInnerProps<T>) => {
  const control = useTabsControl({
    config,
    defaultTab: activeDefaultTab,
    defaultTabIndex: activeDefaultTabIndex,
    onChange,
    tab: activeTab,
    tabIndex: activeTabIndex,
  });

  const translateLabel = useTranslateLabel();

  const renderCallbacks = {
    tabContent: (value: T | null | undefined) => (value == null ? null : renderTabContent?.(value)),
    tabPanelContent: (value: T | null | undefined) =>
      value == null ? null : renderTabPanelContent?.(value),
  };

  const isActive = (tabConfig: TabConfigItem<T>, index: number) => {
    if (!control.isControlled) return undefined;

    return index === control.currentTab?.index || tabConfig.value === control.currentTab?.value;
  };

  const resolveTabLabel = (tabConfig: TabConfigItem<T>) =>
    tabConfig.label ??
    translateLabel(tabConfig.labelKey) ??
    renderCallbacks.tabContent(tabConfig.value);

  const resolveTabPanelContent = (tabConfig: TabConfigItem<T>) =>
    tabConfig.content ?? tabConfig.children ?? renderCallbacks.tabPanelContent(tabConfig.value);

  const tabNodes = (() => {
    const nodes: TabNodes = {
      tabPanels: [],
      tabs: [],
    };

    config.list.forEach((tabConfig, index) => {
      const {
        active,
        children: _tabChildren,
        content,
        label,
        labelKey,
        ...tabNodeProps
      } = tabConfig;

      const key = tabConfig.value ?? index;

      const tabNode = (
        <TabsMenuItem key={key} {...tabNodeProps} active={isActive(tabConfig, index)}>
          {resolveTabLabel(tabConfig)}
        </TabsMenuItem>
      );

      nodes.tabs.push(tabNode);

      if (hidePanelContent) return;

      const tabPanelNode = (
        <Tabs.Panel key={key} value={tabConfig.value}>
          {resolveTabPanelContent(tabConfig)}
        </Tabs.Panel>
      );

      nodes.tabPanels.push(tabPanelNode);
    });

    return nodes;
  })();

  const listContentNode =
    !!beforeList || !!tabNodes.tabs.length ? (
      <>
        {beforeList}
        {tabNodes.tabs}
      </>
    ) : null;

  return (
    <TabsMenuContextProvider>
      <Tabs {...control.tabs}>
        <TabsMenuListContainer className={classNames({ [DIVIDER_CLASS]: divider })}>
          {!!listContentNode && <Tabs.List>{listContentNode}</Tabs.List>}
          <TabsMenuActionsSlot className="shrink-0">{actions}</TabsMenuActionsSlot>
        </TabsMenuListContainer>
        {tabNodes.tabPanels}
        {children}
      </Tabs>
    </TabsMenuContextProvider>
  );
};

const TabsMenuWithNav = typedForwardRef(
  <T extends ValueType>(
    {
      activeDefaultTab,
      activeDefaultTabIndex,
      activeTab,
      activeTabIndex,
      disableKeyboardNav,
      onChange,
      renderTabContent,
      renderTabPanelContent,
      tabConfigs,
      ...rest
    }: TabsMenuProps<T>,
    ref: React.Ref<TabsMenuWithNavHandle<T>>,
  ) => {
    const innerTabConfigs = tabConfigs?.map((config, index) => ({
      ...config,
      value: index,
    }));

    const innerConfig = useTabsConfigList(innerTabConfigs);
    const outerConfig = useTabsConfigList(tabConfigs);

    const indexControl = useTabsIndexControl({
      config: outerConfig,
      defaultTab: activeDefaultTab,
      defaultTabIndex: activeDefaultTabIndex,
      onChange,
      tab: activeTab,
      tabIndex: activeTabIndex,
    });

    const [stepper, stepperHelpers] = useListStepper(outerConfig.list, {
      index: indexControl.tab,
      onIndexChange: (data) => indexControl.onChange(data.index),
      overflowMode: 'loop',
    });

    useTabsNavigationEvents(
      {
        onDigit: (e, digit) => {
          const index = digit - 1;

          if (!outerConfig.list[index]) return;

          e.preventDefault();
          indexControl.onChange(index);
        },
        onNext: stepperHelpers.next,
        onPrev: stepperHelpers.prev,
      },
      {
        enabled: disableKeyboardNav !== true,
      },
    );

    useImperativeHandle(ref, () => ({
      goToTab: (query) => {
        const index = 'index' in query ? query.index : outerConfig.valueToIndex(query.value);

        indexControl.onChange(index);
      },
    }));

    const renderByIndexAdapter = (renderer: ((value: T) => React.ReactNode) | undefined) => {
      if (!renderer) return undefined;

      return (index: number) => {
        const value = outerConfig.indexToValue(index);

        return value === null ? null : renderer(value);
      };
    };

    return (
      <BaseTabsMenu
        {...rest}
        activeTab={stepper.currentIndex}
        config={innerConfig}
        renderTabContent={renderByIndexAdapter(renderTabContent)}
        renderTabPanelContent={renderByIndexAdapter(renderTabPanelContent)}
        onChange={indexControl.onChange}
      />
    );
  },
  DISPLAY_NAMES.WITH_NAV,
);

const TabsMenu = Object.assign(TabsMenuWithNav, {
  Actions: TabsMenuActions,
  ActionsContainer: TabsMenuActionsContainer,
  ActionsSlot: TabsMenuActionsSlot,
  Base: BaseTabsMenu,
  Container: Tabs,
  List: Tabs.List,
  ListContainer: TabsMenuListContainer,
  ListItem: TabsMenuItem,
  Provider: TabsMenuContextProvider,
});

export default TabsMenu;
